import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Search } from 'lucide-react';
import { Card } from '@/components/ui/card';
import SearchResultRow from './SearchResultRow';
import { useSearchResultSelection } from '@hooks/useSearchResultSelection';
import {
  getRelevanceBadgeLabel,
  groupResultsByFile,
  type GroupedResult,
  type RelevanceBadgeLabels,
  type SearchResult,
} from './searchResults';
import { cn } from '@/lib/utils';

// Re-exported so NotionStyleChatPanel, the message list and the grouping tests
// import these from one module.
export type { SearchResult, GroupedResult };
export { groupResultsByFile, getRelevanceBadgeLabel };

interface SearchResultsCardProps {
  query: string;
  results: SearchResult[];
  onResultClick?: (result: SearchResult, query: string) => void;
  onAddToContext?: (results: SearchResult[]) => Promise<void>;
  selectedFileIds?: string[];
}

const SearchResultsCard: React.FC<SearchResultsCardProps> = ({
  query,
  results,
  onResultClick,
  onAddToContext,
  selectedFileIds = [],
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);
  const selection = useSearchResultSelection({ onAddToContext, selectedFileIds });

  const groups = useMemo(() => groupResultsByFile(results), [results]);

  const labels = useMemo<RelevanceBadgeLabels>(
    () => ({
      prefix: t('chat.relevance'),
      high: t('chat.relevanceHigh'),
      medium: t('chat.relevanceMedium'),
      low: t('chat.relevanceLow'),
    }),
    [t],
  );

  const sourceLabel = useMemo(() => {
    const web = results.some((r) => r.type === 'web');
    const internal = results.some((r) => r.type !== 'web');
    if (web) return internal ? t('chat.sourceLabelMixed') : t('chat.sourceLabelWeb');
    return t('chat.sourceLabelInternal');
  }, [results, t]);

  // Numbering follows visible order — primary then its segments, per group — so
  // badges stay stable when a card is expanded. A caller-supplied citationIndex
  // wins, since the model's [n] markers refer to those.
  const fallback = useMemo(() => {
    const map = new Map<SearchResult, number>();
    let counter = 0;
    groups.forEach((group) => {
      counter += 1;
      map.set(group.primary, counter);
      group.additional.forEach((segment) => {
        counter += 1;
        map.set(segment, counter);
      });
    });
    return map;
  }, [groups]);

  const citationLabel = (result: SearchResult) => {
    const n = result.citationIndex ?? fallback.get(result) ?? 1;
    return result.type === 'web' ? `[web:${n}]` : `[${n}]`;
  };

  return (
    <Card className="@container overflow-hidden text-sm shadow-sm transition-shadow hover:shadow-md">
      <div className="flex items-center gap-2 px-3 py-2">
        <Search aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" strokeWidth={1.8} />
        {/* The raw passage count, not the number of grouped files. */}
        <span className="shrink-0 text-xs font-medium revy-figure">
          {t('chat.searchResultsCount', { count: results.length })}
        </span>
        <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">{sourceLabel}</span>
        <button
          type="button"
          onClick={() => setExpanded((open) => !open)}
          aria-expanded={expanded}
          aria-label={t('chat.searchedFor')}
          className="shrink-0 rounded-sm p-1 text-muted-foreground hover:bg-accent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ChevronDown
            className={cn('size-4 transition-transform', !expanded && '-rotate-90')}
            strokeWidth={2}
          />
        </button>
      </div>

      <p className="flex gap-1 px-3 pb-2 text-xs">
        <span className="shrink-0 text-muted-foreground">{t('chat.searchedFor')}</span>
        <span className="min-w-0 truncate">&quot;{query}&quot;</span>
      </p>

      {expanded && groups.length > 0 && (
        <ul className="max-h-[40vh] overflow-y-auto overflow-x-hidden border-t p-1 @sm:max-h-[260px]">
          {groups.map((group) => (
            <SearchResultRow
              key={group.fileId}
              group={group}
              labels={labels}
              citationLabel={citationLabel}
              isSelected={selection.isSelected(group.fileId)}
              isInContext={selection.isInContext(group)}
              showsSegments={selection.showsSegments(group.fileId)}
              onToggleSelected={() => selection.toggleSelected(group)}
              onToggleSegments={() => selection.toggleSegments(group.fileId)}
              onResultClick={onResultClick && ((result) => onResultClick(result, query))}
            />
          ))}
        </ul>
      )}
    </Card>
  );
};

export default SearchResultsCard;
